import React, { useMemo, useState } from "react"
import {
  Button,
  CustomDrawer,
  StaticSnackbar
} from "../../../mp-shared-components/generic-shared-components/components"
import FeedbackTrayForm, { FeedbackForm } from "./FeedbackTrayForm"
import { User } from "../../../mp-database"
import Loader from "../../../mp-utils/ready-utils/components/Loader"
import { FaIcon } from "../../icons"
import { FeedbackPrompt } from "../../../../types/gqlTypes/graphql"
import { Box } from "@mui/material"
import { getWorkkeysReplaceTerm } from "../../../reporting/helpers"

const CAPTURED_FEEDBACK_MESSAGE = `We've captured your feedback.`

type Props = {
  open: boolean
  user: User
  feedbackPrompt?: FeedbackPrompt
  submitLoading?: boolean
  feedbackLoading?: boolean
  feedbackTimeoutDisabled?: boolean
  onClose: () => void
  onSubmit: (data: FeedbackForm) => void
}

const FORM_ID = "feedback-tray-form"

const FeedbackTray: React.FC<Props> = ({
  open,
  user,
  feedbackPrompt,
  submitLoading = false,
  feedbackLoading = false,
  feedbackTimeoutDisabled = false,
  onClose,
  onSubmit
}) => {
  const [isDirty, setDirty] = useState<boolean>(false)

  const isSubmitButtonDisabled =
    feedbackTimeoutDisabled ||
    submitLoading ||
    feedbackLoading ||
    !feedbackPrompt?.responseId ||
    !isDirty

  const promptText = useMemo(() => {
    if (!feedbackPrompt?.promptText) {
      return null
    }

    return feedbackPrompt.promptText.replaceAll(
      "MasteryPrep platform",
      getWorkkeysReplaceTerm("masteryprep")
    )
  }, [feedbackPrompt?.promptText])

  return (
    <CustomDrawer
      open={open}
      onClose={onClose}
      title="Give Feedback"
      actions={
        <>
          <Button onClick={onClose} color="primary" variant="text">
            Close
          </Button>
          <Button
            bold
            color="primary"
            variant="text"
            type="submit"
            form={FORM_ID}
            disabled={isSubmitButtonDisabled}
            startIcon={submitLoading && <FaIcon spin name="spinner-third" />}
          >
            Submit
          </Button>
        </>
      }
    >
      {feedbackLoading ? (
        <Loader />
      ) : (
        <>
          {!!feedbackTimeoutDisabled && (
            <Box pt={3} px={7}>
              <StaticSnackbar
                size="md"
                severity="info"
                message={CAPTURED_FEEDBACK_MESSAGE}
              />
            </Box>
          )}

          {!!feedbackPrompt?.responseId && (
            <FeedbackTrayForm
              id={FORM_ID}
              user={user}
              promptText={promptText}
              disabled={feedbackTimeoutDisabled}
              onSubmit={onSubmit}
              onChangeTouched={setDirty}
            />
          )}
        </>
      )}
    </CustomDrawer>
  )
}

export default FeedbackTray
